<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Quill from 'quill';
import HtmlEditButton from 'quill-html-edit-button';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

if (!Quill.imports['modules/htmlEditButton']) {
  Quill.register('modules/htmlEditButton', HtmlEditButton);
}
const props = defineProps(['modelValue', 'readonly', 'modules', 'classEditor', 'placeholder']);

const emits = defineEmits<{ 'update:modelValue': [value: string] }>();

const editorContainer = ref<HTMLDivElement | null>(null);
let quill: Quill | null = null;

const toolbarOptions = ref([
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ font: [] }],
  ['bold', 'italic', 'underline'],
  [{ script: 'sub' }, { script: 'super' }],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link', 'image', 'code-block'],
  ['clean']
]);

const options = ref({
  theme: 'snow',
  placeholder: props.placeholder,
  readOnly: props.readonly,
  modules: {
    toolbar: toolbarOptions.value,
    htmlEditButton: {
      buttonHTML: '&lt;html/&gt;'
    }
  }
});

onMounted(async () => {
  if (editorContainer.value) {
    quill = new Quill(editorContainer.value, options.value);
    const delta = quill.clipboard.convert({ html: props.modelValue });
    quill.setContents(delta);
    await quill.on('text-change', () => {
      emits('update:modelValue', changedValue(quill?.root.innerHTML ?? ''));
    });
  }
});

const changedValue = (htmlString: string) => {
  if (htmlString.includes('<ol') && htmlString.includes('data-list="bullet"')) {
    const container = document.createElement('div');
    container.innerHTML = htmlString;
    container.querySelectorAll('ol').forEach((ol) => {
      const bulletItems = ol.querySelectorAll('li[data-list="bullet"]');
      if (bulletItems.length) {
        const ul = document.createElement('ul');
        [...ol.attributes].forEach((attr) => ul.setAttribute(attr.name, attr.value));
        while (ol.firstChild) {
          ul.appendChild(ol.firstChild);
        }
        ol.parentNode.replaceChild(ul, ol);
      }
    });
    return container.innerHTML;
  } else {
    return htmlString;
  }
};
</script>
<template>
  <div class="html-editor">
    <div ref="editorContainer"></div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .ql-toolbar.ql-snow {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px 6px 0px 0px;
  }
  .ql-container.ql-snow {
    border: 1px solid #dee2e6;
    border-radius: 0 0 6px 6px;
    height: 15rem;
  }
}
</style>
